import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  Check,
  //  Check, Stethoscope
} from 'lucide-react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import TeacherDiagnosticDashboardChartWrapper from './TeacherDiagnosticDashboardChartWrapper';
import { DiagnosticsStatusByClassReportQuery } from '../../../graphQLOperations/__generated__/DiagnosticsStatusByClassReportQuery.graphql';
import { PreloadedQuery } from 'react-relay/relay-hooks/EntryPointTypes';
import { DiagnosticsStatusByTargetGroupReportQuery } from '../../../graphQLOperations/__generated__/DiagnosticsStatusByTargetGroupReportQuery.graphql';
import TeacherDashboardDiagnosticWidgetEmpty from './TeacherDashboardDiagnosticWidgetEmpty';
import { useClassFocusOnboarding } from '../../../contexts/ClassFocusOnboardingContext';
import { haveAllStudentsCompletedDiagnostic, isDiagnosticReportEmpty } from './helper/chartHelper';
import { NUDGE_TEXT } from './helper/diagnosticNudgeHelper';
import { useDiagnosticStatusReport } from '../../../custom-hooks/useDiagnosticStatusReport';
import { useDiagnosticWidgetNudge } from '../../../custom-hooks/useDiagnosticWidgetNudge';
import { useHideDiagnosticWidget } from '../../../custom-hooks/useHideDiagnosticWidget';
import HideDiagnosticWidgetDialog from './HideDiagnosticWidgetDialog';
import DiagnosticWidgetNudge, { NUDGE_OUTSIDE_SPACE_PX } from './DiagnosticWidgetNudge';

interface Props {
  queryDiagnosticStatusByClassReportRef: PreloadedQuery<DiagnosticsStatusByClassReportQuery, Record<string, unknown>>;
  queryDiagnosticStatusByTargetGoupReportRef: PreloadedQuery<
    DiagnosticsStatusByTargetGroupReportQuery,
    Record<string, unknown>
  >;
  isSiteAdmin: boolean;
  isFilteredByRoadmap: boolean;
  /**
   * The single class the report covers, or null on the roadmap view / with several
   * classes selected. Hiding targets one class, so without it there is nothing to hide
   * and no class to remember a nudge against.
   */
  activeClassId: string | null;
  onWidgetHidden: (classId: string) => void;
}

export const TeacherDiagnosticDashboard: React.FC<Props> = ({
  queryDiagnosticStatusByClassReportRef,
  queryDiagnosticStatusByTargetGoupReportRef,
  isSiteAdmin,
  isFilteredByRoadmap,
  activeClassId,
  onWidgetHidden,
}) => {
  const navigate = useNavigate();
  const { setAnyStudentCompletedDiagnostic } = useClassFocusOnboarding();
  const [hideDialogVisible, setHideDialogVisible] = useState(false);

  const diagnosticResults = useDiagnosticStatusReport({
    isSiteAdmin,
    isFilteredByRoadmap,
    queryDiagnosticStatusByClassReportRef,
    queryDiagnosticStatusByTargetGoupReportRef,
  });

  const diagnosticEmpty = useMemo(() => isDiagnosticReportEmpty(diagnosticResults), [diagnosticResults]);
  const allStudentsCompletedDiagnostic = useMemo(
    () => haveAllStudentsCompletedDiagnostic(diagnosticResults),
    [diagnosticResults],
  );

  const { activeNudge, dismissNudge, canHideWidget } = useDiagnosticWidgetNudge({
    diagnosticResults,
    classId: activeClassId,
    isSiteAdmin,
    enabled: !diagnosticEmpty && !allStudentsCompletedDiagnostic,
  });

  // The bubble hangs off the widget's right edge, where the space available depends on the
  // dashboard layout: the onboarding guide column leaves plenty, but once it is dismissed
  // the widget spans the full width and only the page margin is left — not enough, so the
  // bubble rendered off-screen. Measure rather than assume, and tuck it inside when tight.
  const containerRef = useRef<HTMLDivElement>(null);
  const [nudgeFitsOutside, setNudgeFitsOutside] = useState(true);

  useLayoutEffect(() => {
    if (!activeNudge) return;

    const measure = () => {
      if (!containerRef.current) return;

      const { right } = containerRef.current.getBoundingClientRect();
      setNudgeFitsOutside(window.innerWidth - right >= NUDGE_OUTSIDE_SPACE_PX);
    };

    measure();
    window.addEventListener('resize', measure);

    return () => window.removeEventListener('resize', measure);
  }, [activeNudge]);

  const hideDiagnosticWidget = useHideDiagnosticWidget();

  const handleHideWidget = () => {
    if (!activeClassId) return;

    hideDiagnosticWidget(activeClassId);
    onWidgetHidden(activeClassId);
  };

  useEffect(() => {
    setAnyStudentCompletedDiagnostic(
      diagnosticResults?.some(el => el.diagnosticStatus === 'DIAGNOSTIC_SUBMITTED') ?? false,
    );
  }, [diagnosticResults, setAnyStudentCompletedDiagnostic]);

  return (
    <div ref={containerRef} className="tw-relative tw-pb-[45px]">
      <HideDiagnosticWidgetDialog
        visible={hideDialogVisible}
        onHide={() => setHideDialogVisible(false)}
        onConfirm={handleHideWidget}
      />

      {activeNudge && (
        <DiagnosticWidgetNudge
          text={NUDGE_TEXT[activeNudge]}
          onDismiss={dismissNudge}
          className={`tw-absolute tw-bottom-[56px] ${nudgeFitsOutside ? 'tw-left-full tw-ml-4' : 'tw-right-4'}`}
        />
      )}

      <Card
        pt={{
          footer: { className: 'tw-ml-auto' },
        }}
        className={`tw-h-full nc_block tw-p-0 tw-my-4 !tw-shadow-none  ${
          !diagnosticEmpty ? '!tw-bg-[#FAFBFF] !tw-border-indigo-200' : ''
        }`}
        header={() => (
          <h2 className="tw-text-lg tw-p-2 tw-font-bold tw-not-italic tw-border-b-[2px] tw-border-t-[0px] tw-border-x-[0px] tw-border-neutral-100 tw-border-solid">
            Diagnostic
          </h2>
        )}
        footer={
          !diagnosticEmpty ? (
            <div className="tw-flex tw-flex-row tw-gap-x-2 tw-items-center tw-justify-end tw-p-2">
              {/* Mutually exclusive: `canHideWidget` already requires an unfinished diagnostic. */}
              {activeClassId && allStudentsCompletedDiagnostic && (
                <Button
                  label="All done! Hide this widget"
                  icon={<Check size={16} />}
                  className="tw-gap-x-2 tw-self-start tw-flex tw-flex-row-reverse"
                  onClick={() => setHideDialogVisible(true)}
                />
              )}
              {canHideWidget && (
                <>
                  <span className="tw-text-sm tw-italic tw-font-atkinson tw-text-neutral-950">Ready to move on!</span>
                  <Button
                    label="Hide this widget"
                    icon={<Check size={16} />}
                    className="tw-gap-x-2 tw-self-start tw-flex tw-flex-row-reverse"
                    onClick={() => setHideDialogVisible(true)}
                  />
                </>
              )}
              <Button
                label="Review results"
                icon={<ArrowRight size={16} />}
                className="tw-gap-x-2 tw-self-start tw-flex tw-flex-row-reverse"
                link
                onClick={() => navigate('/diagnostics')}
              />
            </div>
          ) : (
            <></>
          )
        }
      >
        {diagnosticEmpty ? (
          <TeacherDashboardDiagnosticWidgetEmpty />
        ) : (
          <TeacherDiagnosticDashboardChartWrapper diagnosticResults={diagnosticResults} />
        )}
      </Card>
    </div>
  );
};

export default TeacherDiagnosticDashboard;
